import { createContext, PropsWithChildren, useContext, useEffect, useState } from "react";
import { useRequest } from "./RequestContext";

export interface CatalogContextProps {
    diseaseCatalog: DiseaseCatalogModel[];
    medicineCatalog: MedicineCatalogModel[];
    foodCatalog: FoodCatalogModel[];
    antibioticCatalog: AntibioticCatalogModel[];
    stateCatalog: StateCatalogModel[];
    blogCatalog: BlogModel[];
    handleClickGetDiseaseCatalog: () => Promise<void>;
    handleClickGetMedicineCatalog: () => Promise<void>;
    handleClickGetFoodCatalog: () => Promise<void>;
    handleClickGetAntibioticCatalog: () => Promise<void>;
    handleClickGetStateCatalog: () => Promise<void>;
    handleClickGetBlogCatalog: () => Promise<void>;
}

export const CatalogContext = createContext<CatalogContextProps | null>(null);

export const CatalogProvider = ({ children }: PropsWithChildren) => {
    const { sendRequest } = useRequest();

    const [diseaseCatalog, setDiseaseCatalog] = useState<DiseaseCatalogModel[]>([]);
    const [medicineCatalog, setMedicineCatalog] = useState<MedicineCatalogModel[]>([]);
    const [foodCatalog, setFoodCatalog] = useState<FoodCatalogModel[]>([]);
    const [antibioticCatalog, setAntibioticCatalog] = useState<AntibioticCatalogModel[]>([]);
    const [stateCatalog, setStateCatalog] = useState<StateCatalogModel[]>([]);
    const [blogCatalog, setBlogCatalog] = useState<BlogModel[]>([]);

    const handleClickGetDiseaseCatalog = async () => {
        const response = await sendRequest<DiseaseCatalogModel[]>({
            path: "/api/enfermedad/menu",
            hideLoader: true,
        });

        if (response.hasError) return;

        setDiseaseCatalog(response.data);
    };

    const handleClickGetMedicineCatalog = async () => {
        const response = await sendRequest<MedicineCatalogModel[]>({
            path: "/api/medicamentos/menu",
            hideLoader: true,
        });

        if (response.hasError) return;

        setMedicineCatalog(response.data);
    };

    const handleClickGetFoodCatalog = async () => {
        const response = await sendRequest<FoodCatalogModel[]>({
            path: "/api/alimento/menu",
            hideLoader: true,
        });

        if (response.hasError) return;

        setFoodCatalog(response.data);
    };

    const handleClickGetAntibioticCatalog = async () => {
        const response = await sendRequest<AntibioticCatalogModel[]>({
            path: "/api/antibiotico/menu",
            hideLoader: true,
        });

        if (response.hasError) return;

        setAntibioticCatalog(response.data);
    };

    const handleClickGetStateCatalog = async () => {
        const response = await sendRequest<StateCatalogModel[]>({
            path: "/api/estados",
            hideLoader: true,
        });

        if (response.hasError) return;

        setStateCatalog(response.data);
    };

    const handleClickGetBlogCatalog = async () => {
        const response = await sendRequest<BlogModel[]>({
            path: "/api/blog/menu",
            hideLoader: true,
        });

        if (response.hasError) return;

        setBlogCatalog(response.data);
    };

    useEffect(() => {
        if (diseaseCatalog.length === 0) handleClickGetDiseaseCatalog();
        if (medicineCatalog.length === 0) handleClickGetMedicineCatalog();
        if (foodCatalog.length === 0) handleClickGetFoodCatalog();
        if (antibioticCatalog.length === 0) handleClickGetAntibioticCatalog();
        if (stateCatalog.length === 0) handleClickGetStateCatalog();
        if (blogCatalog.length === 0) handleClickGetBlogCatalog();
    }, []);

    return (
        <CatalogContext.Provider
            value={{
                antibioticCatalog,
                diseaseCatalog,
                foodCatalog,
                handleClickGetAntibioticCatalog,
                handleClickGetDiseaseCatalog,
                handleClickGetFoodCatalog,
                handleClickGetMedicineCatalog,
                handleClickGetStateCatalog,
                medicineCatalog,
                stateCatalog,
                blogCatalog,
                handleClickGetBlogCatalog,
            }}
        >
            {children}
        </CatalogContext.Provider>
    );
};

export const useCatalog = () => useContext(CatalogContext) as CatalogContextProps;

export interface DiseaseCatalogModel {
    idEnfermedad: string;
    nombreEnfermedad: string;
    mostrarMenu: boolean;
    fechaRegistro: string;
}

export interface MedicineCatalogModel {
    idMedicamento: string;
    nombreMedicamento: string;
    idCategoriaMedicamento: string;
    nombreCategoriaMedicamento: string;
    mostrarMenu: boolean;
    fechaRegistro: string;
}

export interface FoodCatalogModel {
    idAlimento: string;
    nombreAlimento: string;
    mostrarMenu: boolean;
    fechaRegistro: string;
}

export interface AntibioticCatalogModel {
    idAntibiotico: string;
    nombreAntibiotico: string;
    mostrarMenu: boolean;
    fechaRegistro: string;
}

export interface StateCatalogModel {
    idEstado: string;
    nombreEstado: string;
    fechaRegistro: string;
}

export interface BlogModel {
    idBlog: string;
    titulo: string;
    autor: string;
    fechaPublicacion: string;
    contenido: string;
    visible: boolean;
    fechaRegistro: string;
    activo: boolean;
}
